import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Fleet } from "../lib/api";
import {
  ArrowLeft, Plus, Trash2, Wrench, ShieldCheck, CalendarClock, Save,
} from "lucide-react";
import { toast } from "sonner";
import VehicleAnalytics from "../components/VehicleAnalytics";
import { useConfirm } from "../components/ConfirmProvider";

const EMPTY_REPAIR = {
  date: new Date().toISOString().slice(0, 10),
  description: "",
  mileage: "",
  cost: "",
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((new Date(dateStr) - today) / 86400000);
}

function statusFor(date) {
  if (!date) return { cls: "bg-zinc-100 text-zinc-600 border-zinc-200", label: "—" };
  const d = daysUntil(date);
  if (d < 0) return { cls: "bg-red-100 text-red-800 border-red-300", label: "przeterminowane" };
  if (d <= 30) return { cls: "bg-amber-100 text-amber-800 border-amber-300", label: `za ${d} dni` };
  return { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "aktualne" };
}

export default function VehicleDetail() {
  const { id } = useParams();
  const { confirm } = useConfirm();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [repair, setRepair] = useState(EMPTY_REPAIR);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const data = await Fleet.get(id);
      setVehicle(data);
    } catch {
      toast.error("Nie znaleziono pojazdu");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line

  const addRepair = async (e) => {
    e.preventDefault();
    if (!repair.description) {
      toast.error("Opis naprawy jest wymagany");
      return;
    }
    setAdding(true);
    try {
      const updated = await Fleet.addRepair(id, {
        date: repair.date,
        description: repair.description,
        mileage: repair.mileage === "" ? null : Number(repair.mileage),
        cost: repair.cost === "" ? null : Number(repair.cost),
      });
      setVehicle(updated);
      setRepair(EMPTY_REPAIR);
      toast.success("Naprawa dodana");
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    } finally {
      setAdding(false);
    }
  };

  const removeRepair = async (repairId) => {
    if (!(await confirm("Usunąć ten wpis?", { tone: "danger", confirmLabel: "Usuń" }))) return;
    try {
      await Fleet.removeRepair(id, repairId);
      toast.success("Wpis usunięty");
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return <div className="py-20 text-center text-zinc-500">Ładowanie...</div>;
  }

  if (!vehicle) {
    return (
      <div className="py-20 text-center">
        <p>Nie znaleziono pojazdu.</p>
        <Link
          to="/fleet"
          className="inline-block mt-6 px-6 py-3 border border-zinc-900 font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white"
        >
          Wróć do floty
        </Link>
      </div>
    );
  }

  const insStatus = statusFor(vehicle.insurance_expiry);
  const insStatusInspection = statusFor(vehicle.inspection_expiry);
  const repairs = (vehicle.repairs || []).slice().sort((a, b) =>
    (b.date || "").localeCompare(a.date || "")
  );
  const totalCost = repairs.reduce((acc, r) => acc + (r.cost || 0), 0);

  return (
    <div className="space-y-8" data-testid="vehicle-detail-page">
      <Link
        to="/fleet"
        data-testid="back-to-fleet"
        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900"
      >
        <ArrowLeft className="w-3 h-3" /> Flota
      </Link>

      <div className="border border-zinc-900 p-8">
        <div className="font-label mb-2">// Pojazd</div>
        <h1 className="font-heading font-black text-4xl uppercase tracking-tight">
          {vehicle.brand} {vehicle.model}
        </h1>
        <div className="font-mono text-sm text-zinc-500 mt-2">
          {vehicle.registration} · VIN {vehicle.vin || "—"} · Rok {vehicle.year || "—"}
        </div>
        {vehicle.notes && (
          <p className="mt-4 text-zinc-700 text-sm max-w-2xl">{vehicle.notes}</p>
        )}
      </div>

      {/* Documents status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-zinc-900">
        <DocCard
          icon={ShieldCheck}
          label="Ubezpieczenie OC/AC"
          date={vehicle.insurance_expiry}
          status={insStatus}
          testId="doc-insurance"
        />
        <DocCard
          icon={CalendarClock}
          label="Przegląd techniczny"
          date={vehicle.inspection_expiry}
          status={insStatusInspection}
          dark
          testId="doc-inspection"
        />
      </div>

      <VehicleAnalytics vehicleId={id} />

      {/* Repair log */}
      <section className="space-y-6">
        <div className="flex items-end justify-between">
          <div>
            <div className="font-label mb-1">// Ewidencja</div>
            <h2 className="font-heading font-black text-2xl uppercase tracking-tight flex items-center gap-3">
              <Wrench className="w-6 h-6 text-brand" /> Naprawy ({repairs.length})
            </h2>
            {totalCost > 0 && (
              <div className="text-sm text-zinc-500 mt-1 font-mono">
                Suma kosztów: <strong>{totalCost.toFixed(2)} zł</strong>
              </div>
            )}
          </div>
        </div>

        <form
          onSubmit={addRepair}
          data-testid="add-repair-form"
          className="border border-zinc-900 p-6 grid grid-cols-1 md:grid-cols-12 gap-4"
        >
          <div className="md:col-span-2">
            <span className="font-label block mb-2">Data</span>
            <input
              type="date"
              data-testid="repair-input-date"
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-brand"
              value={repair.date}
              onChange={(e) => setRepair({ ...repair, date: e.target.value })}
              required
            />
          </div>
          <div className="md:col-span-5">
            <span className="font-label block mb-2">Co było robione *</span>
            <input
              type="text"
              data-testid="repair-input-description"
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"
              value={repair.description}
              onChange={(e) =>
                setRepair({ ...repair, description: e.target.value })
              }
              placeholder="np. wymiana klocków hamulcowych przód"
              required
            />
          </div>
          <div className="md:col-span-2">
            <span className="font-label block mb-2">Przebieg (km)</span>
            <input
              type="number"
              data-testid="repair-input-mileage"
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-brand"
              value={repair.mileage}
              onChange={(e) => setRepair({ ...repair, mileage: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <span className="font-label block mb-2">Koszt (zł)</span>
            <input
              type="number"
              step="0.01"
              data-testid="repair-input-cost"
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-brand"
              value={repair.cost}
              onChange={(e) => setRepair({ ...repair, cost: e.target.value })}
            />
          </div>
          <div className="md:col-span-1 flex items-end">
            <button
              type="submit"
              disabled={adding}
              data-testid="btn-add-repair"
              className="w-full inline-flex items-center justify-center gap-1 bg-brand text-white px-3 py-2 font-bold uppercase tracking-wider hover:bg-brand-hover disabled:bg-zinc-300"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </form>

        <div className="border border-zinc-900 overflow-x-auto">
          <table className="w-full" data-testid="repairs-table">
            <thead>
              <tr className="bg-zinc-900 text-white text-left">
                <Th>Data</Th>
                <Th>Opis</Th>
                <Th>Przebieg</Th>
                <Th>Koszt</Th>
                <Th className="text-right">Akcje</Th>
              </tr>
            </thead>
            <tbody>
              {repairs.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="py-12 text-center text-zinc-500 text-sm"
                    data-testid="empty-repairs"
                  >
                    Brak wpisów. Dodaj pierwszą naprawę powyżej.
                  </td>
                </tr>
              )}
              {repairs.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-zinc-200 hover:bg-zinc-50"
                  data-testid={`repair-row-${r.id}`}
                >
                  <td className="py-3 px-4 font-mono text-sm">
                    {new Date(r.date).toLocaleDateString("pl-PL")}
                  </td>
                  <td className="py-3 px-4 text-sm">{r.description}</td>
                  <td className="py-3 px-4 font-mono text-sm">
                    {r.mileage != null ? `${r.mileage.toLocaleString("pl-PL")} km` : "—"}
                  </td>
                  <td className="py-3 px-4 font-mono text-sm font-semibold">
                    {r.cost != null ? `${r.cost.toFixed(2)} zł` : "—"}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() => removeRepair(r.id)}
                      data-testid={`btn-delete-repair-${r.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 text-red-600 hover:border-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function DocCard({ icon: Icon, label, date, status, dark, testId }) {
  return (
    <div
      data-testid={testId}
      className={`p-6 border-r border-zinc-900 last:border-r-0 ${
        dark ? "bg-zinc-900 text-white" : "bg-white text-zinc-900"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className={`font-label ${dark ? "text-zinc-400" : "text-zinc-500"}`}>
          {label}
        </div>
        <Icon className="w-4 h-4 opacity-60" />
      </div>
      <div className="mt-4 font-heading font-black text-2xl">
        {date ? new Date(date).toLocaleDateString("pl-PL") : "Brak daty"}
      </div>
      <div className={`mt-3 inline-block px-2 py-0.5 border text-xs font-mono ${status.cls}`}>
        {status.label}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`py-3 px-4 text-xs uppercase tracking-[0.2em] font-bold ${className}`}>
      {children}
    </th>
  );
}
